'use client'

import { useCallback, useEffect } from 'react'
import { useRouter } from 'next/navigation'
import { useAuthStore } from '@/lib/store/auth-store'
import { usePermissions, PERMISSIONS } from '@/lib/permissions'

type Permission = typeof PERMISSIONS[keyof typeof PERMISSIONS]

export interface AuthGuardOptions {
  redirectTo?: string
  requireAuth?: boolean
  requireRole?: string[]
  onUnauthorized?: () => void
  onSuccess?: () => void
}

export interface AuthGuardActions {
  navigateAfterLogin: () => void
  navigateAfterLogout: () => void
  navigateToLogin: (_redirectTo?: string) => void
  navigateToDashboard: () => void
  navigateWithPermissionCheck: (_path: string, _requiredPermissions?: string[]) => boolean
  navigateToPublicRoute: (_path: string) => void
}

/**
 * Centralized authentication guard hook for safe routing
 * Replaces manual router.push() calls with permission-aware navigation
 */
export function useAuthGuard(options: AuthGuardOptions = {}): AuthGuardActions {
  const router = useRouter()
  const { isAuthenticated, user, hasCompleteAuthState, isRehydrated } = useAuthStore()
  const { can } = usePermissions()

  const {
    redirectTo,
    requireAuth = false,
    requireRole,
    onUnauthorized,
    onSuccess,
  } = options

  // Navigate after successful login with state validation
  const navigateAfterLogin = useCallback(() => {
    const targetPath = redirectTo || '/dashboard'
    const hasComplete = hasCompleteAuthState()

    if (process.env.NODE_ENV === 'production') {
      console.log('AuthGuard: Navigating after login', {
        targetPath,
        userId: user?.id,
        userRole: user?.role,
        isAuthenticated,
        hasCompleteState: hasComplete,
      })
    }

    // Only navigate if we have complete auth state
    if (isAuthenticated && hasComplete) {
      router.push(targetPath)
      onSuccess?.()
    } else {
      // If state is incomplete, wait and try again
      if (process.env.NODE_ENV === 'production') {
        console.log('AuthGuard: Auth state incomplete, delaying navigation')
      }
      setTimeout(() => {
        if (hasCompleteAuthState()) {
          router.push(targetPath)
          onSuccess?.()
        } else {
          console.warn('AuthGuard: Auth state still incomplete after delay, forcing navigation')
          router.push(targetPath)
          onSuccess?.()
        }
      }, 150)
    }
  }, [redirectTo, router, user?.id, user?.role, isAuthenticated, hasCompleteAuthState, onSuccess])

  // Navigate after logout
  const navigateAfterLogout = useCallback(() => {
    const targetPath = '/magic-login'

    if (process.env.NODE_ENV === 'production') {
      console.log('AuthGuard: Navigating after logout')
    }

    router.push(targetPath)
  }, [router])

  // Navigate to login with optional redirect
  const navigateToLogin = useCallback((redirectPath?: string) => {
    const loginUrl = redirectPath
      ? `/magic-login?redirect=${encodeURIComponent(redirectPath)}`
      : '/magic-login'

    if (process.env.NODE_ENV === 'production') {
      console.log('AuthGuard: Navigating to login', {
        loginUrl,
        redirectPath,
        currentAuth: isAuthenticated,
      })
    }

    router.push(loginUrl)
    onUnauthorized?.()
  }, [router, isAuthenticated, onUnauthorized])

  // Navigate to dashboard
  const navigateToDashboard = useCallback(() => {
    // Double-check authentication state before navigating
    const hasComplete = hasCompleteAuthState()
    const token = typeof window !== 'undefined' ? localStorage.getItem('auth-token') : null
    if (!token || !isAuthenticated || !user || !hasComplete) {
      if (process.env.NODE_ENV === 'production') {
        console.log('AuthGuard: Dashboard navigation blocked - invalid auth state', {
          hasToken: !!token,
          isAuthenticated,
          hasUser: !!user,
          hasComplete,
        })
      }
      navigateToLogin()
      return
    }

    if (process.env.NODE_ENV === 'production') {
      console.log('AuthGuard: Navigating to dashboard', {
        isAuthenticated,
        userId: user?.id,
      })
    }

    router.push('/dashboard')
  }, [router, isAuthenticated, user, hasCompleteAuthState, navigateToLogin])

  // Navigate to public routes that don't require authentication
  const navigateToPublicRoute = useCallback((path: string) => {
    if (process.env.NODE_ENV === 'production') {
      console.log('AuthGuard: Navigating to public route', { path })
    }

    router.push(path)
    onSuccess?.()
  }, [router, onSuccess])

  // Navigate with permission checking
  const navigateWithPermissionCheck = useCallback((
    path: string,
    requiredPermissions?: string[]
  ): boolean => {
    // Check authentication and complete state first
    const hasComplete = hasCompleteAuthState()
    if (!isAuthenticated || !user || !hasComplete) {
      if (process.env.NODE_ENV === 'production') {
        console.log('AuthGuard: Auth/state check failed', {
          isAuthenticated,
          hasUser: !!user,
          hasCompleteState: hasComplete,
          path,
        })
      }
      navigateToLogin(path)
      return false
    }

    // Check role requirements
    if (requireRole && !requireRole.includes(user.role)) {
      if (process.env.NODE_ENV === 'production') {
        console.log('AuthGuard: Role check failed', {
          userRole: user.role,
          requiredRoles: requireRole,
          path,
        })
      }

      // Redirect to dashboard instead of login for role issues
      navigateToDashboard()
      onUnauthorized?.()
      return false
    }

    // Check specific permissions
    if (requiredPermissions && !requiredPermissions.every(perm => can(perm as Permission))) {
      if (process.env.NODE_ENV === 'production') {
        console.log('AuthGuard: Permission check failed', {
          requiredPermissions,
          path,
        })
      }

      navigateToDashboard()
      onUnauthorized?.()
      return false
    }

    // All checks passed, navigate
    router.push(path)
    onSuccess?.()
    return true
  }, [
    isAuthenticated,
    user,
    requireRole,
    can,
    router,
    navigateToLogin,
    navigateToDashboard,
    onUnauthorized,
    onSuccess,
    hasCompleteAuthState,
  ])

  // Auto-redirect effect for pages that require authentication
  useEffect(() => {
    // Only redirect if store is rehydrated to prevent flash during SSR hydration
    if (requireAuth && isRehydrated) {
      const hasComplete = hasCompleteAuthState()

      if (!isAuthenticated || !hasComplete) {
        if (process.env.NODE_ENV === 'production') {
          console.log('AuthGuard: Auto-redirect triggered', {
            requireAuth,
            isAuthenticated,
            hasCompleteState: hasComplete,
            redirectTo,
            isRehydrated,
          })
        }
        navigateToLogin(redirectTo)
      }
    }
  }, [requireAuth, isAuthenticated, hasCompleteAuthState, navigateToLogin, redirectTo, isRehydrated])

  return {
    navigateAfterLogin,
    navigateAfterLogout,
    navigateToLogin,
    navigateToDashboard,
    navigateWithPermissionCheck,
    navigateToPublicRoute,
  }
}

/**
 * Shorthand hooks for common auth guard scenarios
 */

// Hook for protected pages that require authentication
export function useProtectedRoute(options: Omit<AuthGuardOptions, 'requireAuth'> = {}) {
  return useAuthGuard({ ...options, requireAuth: true })
}

// Hook for admin-only pages
export function useAdminRoute(options: AuthGuardOptions = {}) {
  return useAuthGuard({ ...options, requireAuth: true, requireRole: ['admin'] })
}

// Hook for auth pages that should redirect authenticated users
export function useAuthPageGuard() {
  const { isAuthenticated, hasCompleteAuthState, isRehydrated } = useAuthStore()
  const authGuard = useAuthGuard()

  useEffect(() => {
    // Only redirect if store is rehydrated and we have complete auth state
    if (isRehydrated && isAuthenticated && hasCompleteAuthState()) {
      if (process.env.NODE_ENV === 'production') {
        console.log('AuthPageGuard: Redirecting authenticated user from auth page')
      }
      authGuard.navigateToDashboard()
    }
  }, [isAuthenticated, hasCompleteAuthState, isRehydrated, authGuard])

  return authGuard
}
